import React, { useState } from 'react';
import { Sparkles, Check, X, AlertCircle, Loader2, Gift, Calendar, Users, Stethoscope } from 'lucide-react';
import { useVoucher } from '../../hooks/useSubscription';

export function VoucherActivation({ 
  userId, 
  onSuccess, 
  onError,
  className = ''
}) {
  const [code, setCode] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    language: 'english',
    privacyConsent: false,
    termsConsent: false,
  });

  const { 
    validationResult, 
    validating, 
    activating, 
    error,
    validate, 
    activateForNewUser,
    activateForExistingUser,
    reset 
  } = useVoucher();

  const formatCode = (value) => {
    return value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
  };

  const handleValidate = async () => {
    if (!code.trim()) return;
    const result = await validate(code.trim(), userId);
    if (result.valid && userId) {
      // For existing users, show activation button directly
    } else if (result.valid && !userId) {
      // For new users, show registration form
      setShowForm(true);
    }
  };

  const handleActivate = async () => {
    try {
      let result;
      
      if (userId) {
        result = await activateForExistingUser(code.trim(), userId);
      } else {
        if (!formData.privacyConsent || !formData.termsConsent) {
          onError?.('Please accept the privacy policy and terms of service');
          return;
        }
        result = await activateForNewUser(
          code.trim(),
          {
            name: formData.name,
            email: formData.email,
            mobile: formData.mobile,
            language: formData.language,
          },
          {
            privacy_policy: formData.privacyConsent,
            terms_of_service: formData.termsConsent,
          }
        );
      }
      
      onSuccess?.(result);
    } catch (err) {
      onError?.(err.message);
    }
  };

  const handleReset = () => {
    setCode('');
    setShowForm(false);
    setFormData({
      name: '',
      email: '',
      mobile: '',
      language: 'english',
      privacyConsent: false,
      termsConsent: false,
    });
    reset();
  };

  return (
    <div className={`bg-slate-800 rounded-2xl shadow-xl overflow-hidden border border-slate-700 ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/20 rounded-xl">
            <Gift size={28} />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Have a Voucher Code?</h2>
            <p className="text-cyan-100 mt-1">
              Activate your voucher for instant access
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Code Input */}
        {!validationResult?.valid && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Enter your voucher code
              </label>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(formatCode(e.target.value))}
                    placeholder="XXXX-XXXXXX"
                    className="w-full px-4 py-3 bg-slate-900 border-2 border-slate-600 rounded-xl focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none text-lg font-mono tracking-wider text-white placeholder-slate-500"
                    maxLength={20}
                  />
                  {validationResult?.valid === false && (
                    <X className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" size={24} />
                  )}
                </div>
                <button
                  onClick={handleValidate}
                  disabled={!code.trim() || validating}
                  className="px-6 py-3 bg-cyan-500 text-white rounded-xl font-semibold hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  {validating ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <Sparkles size={20} />
                  )}
                  Validate
                </button>
              </div>
            </div>

            {/* Error Message */}
            {(error || validationResult?.message) && !validationResult?.valid && (
              <div className="flex items-center gap-2 text-red-400 bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                <AlertCircle size={20} />
                <span>{error || validationResult?.message}</span>
              </div>
            )}
          </div>
        )}

        {/* Valid Voucher Info */}
        {validationResult?.valid && validationResult.voucher && (
          <div className="space-y-6">
            {/* Success Badge */}
            <div className="flex items-center gap-2 text-green-400 bg-green-500/10 p-3 rounded-lg border border-green-500/20">
              <Check size={20} />
              <span className="font-medium">Valid voucher code!</span>
            </div>

            {/* Voucher Details */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
              <h3 className="text-xl font-bold text-white mb-4">
                {validationResult.voucher.name}
              </h3>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-slate-800 rounded-lg border border-slate-700">
                  <Users className="mx-auto text-cyan-400 mb-1" size={24} />
                  <p className="text-2xl font-bold text-white">
                    {validationResult.voucher.max_patients}
                  </p>
                  <p className="text-sm text-slate-400">Patients</p>
                </div>
                <div className="text-center p-3 bg-slate-800 rounded-lg border border-slate-700">
                  <Calendar className="mx-auto text-cyan-400 mb-1" size={24} />
                  <p className="text-2xl font-bold text-white">
                    {validationResult.voucher.validity_days}
                  </p>
                  <p className="text-sm text-slate-400">Days</p>
                </div>
                <div className="text-center p-3 bg-slate-800 rounded-lg border border-slate-700">
                  <Stethoscope 
                    className={`mx-auto mb-1 ${validationResult.voucher.nurse_access ? 'text-green-400' : 'text-slate-500'}`} 
                    size={24} 
                  />
                  <p className={`text-lg font-bold ${validationResult.voucher.nurse_access ? 'text-green-400' : 'text-slate-500'}`}>
                    {validationResult.voucher.nurse_access ? 'Yes' : 'No'}
                  </p>
                  <p className="text-sm text-slate-400">Nurse Access</p>
                </div>
              </div>

              {/* Features */}
              <div className="border-t border-slate-700 pt-4">
                <p className="text-sm font-medium text-slate-400 mb-2">Included Features:</p>
                <div className="flex flex-wrap gap-2">
                  {validationResult.voucher.features.slice(0, 5).map((feature, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded text-sm border border-cyan-500/20">
                      <Check size={14} />
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Registration Form (for new users) */}
            {!userId && showForm && (
              <div className="space-y-4">
                <h4 className="font-semibold text-white">Create your account</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      placeholder="+91..."
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-white placeholder-slate-500"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-white"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.privacyConsent}
                      onChange={(e) => setFormData({ ...formData, privacyConsent: e.target.checked })}
                      className="w-4 h-4 text-cyan-500 rounded border-slate-600 bg-slate-900 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-slate-400">
                      I agree to the <a href="/privacy" className="text-cyan-400 underline">Privacy Policy</a>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.termsConsent}
                      onChange={(e) => setFormData({ ...formData, termsConsent: e.target.checked })}
                      className="w-4 h-4 text-cyan-500 rounded border-slate-600 bg-slate-900 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-slate-400">
                      I agree to the <a href="/terms" className="text-cyan-400 underline">Terms of Service</a>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="px-6 py-3 border border-slate-600 text-slate-300 rounded-xl font-medium hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleActivate}
                disabled={activating || (!userId && (!formData.name || !formData.email || !formData.mobile || !formData.privacyConsent || !formData.termsConsent))}
                className="flex-1 px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              >
                {activating ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Activating...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    Activate Voucher
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Help Text */}
        {!validationResult?.valid && (
          <p className="mt-4 text-sm text-slate-500 text-center">
            Get voucher codes from our partner hospitals, corporate wellness programs, or promotional campaigns.
          </p>
        )}
      </div>
    </div>
  );
}

export default VoucherActivation;
